import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import './calendar-theme.css';
import { App } from './App.tsx';
import { AppStore, setStore } from './store/appStore.ts';
import { IndexedDbStorage } from './store/idb.ts';
import type { StorageAdapter } from './store/storage.ts';
import { NotificationService, setNotificationService } from './notifications/service.ts';
import { isNative } from './platform.ts';
import { SyncClient, setSyncClient } from './sync/syncClient.ts';
import { AuthStore, setAuthStore } from './auth/authStore.ts';
import { LEGACY_DB, REGISTRY_DB, onProfileChange, readRegistry, writeUiPrefs } from './profile/registry.ts';
import { consumeReturnTo } from './ui/uiSession.ts';
import { captureResetToken } from './auth/resetToken.ts';
import { normalizeEntryPath } from './ui/entryPath.ts';
import { fixedClock, systemClock } from '@autoday/core';
import { getTheme } from './ui/theme.ts';
import { getLang } from './i18n/i18n.ts';
import { installNativeBackNavigation } from './ui/nativeBack.ts';

/**
 * Fanns AutoDay redan på den här installationen före denna start? Avgör standardtemat när inget val är
 * sparat (befintlig → mörkt som förut, ny → System). Läser bara; måste köras innan profilregistret skapas.
 */
async function hadExistingInstall(): Promise<boolean> {
  const list = (indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string }>> }).databases;
  if (typeof list === 'function') {
    const names = new Set((await list.call(indexedDB)).map((d) => d.name));
    return names.has(REGISTRY_DB) || names.has(LEGACY_DB);
  }
  return (await readRegistry()) !== null;
}

async function makeStorage(dbName: string): Promise<StorageAdapter> {
  if (isNative()) return new (await import('./store/sqlite.ts')).SqliteStorage(dbName);
  return new IndexedDbStorage(dbName);
}

async function boot() {
  // Före allt annat: en gissad sökväg (t.ex. /logout) skrivs om till / med samma hash – den utför aldrig något.
  const entryIntent = normalizeEntryPath();
  // Flytta en återställningstoken från adressfältet till minnet.
  const resetting = captureResetToken();
  // Tema, palett och språk avgörs innan profilregistret skapas och innan något renderas
  // (theme-boot.js har redan satt preliminära värden). Befintlig installation → samma utseende och svenska.
  await getTheme().start(hadExistingInstall);
  await getLang().start(hadExistingInstall);
  const sqlite = isNative() ? (await import('./store/sqlite.ts')).SqliteStorage : null;
  const storageFor = (dbName: string): StorageAdapter => (sqlite ? new sqlite(dbName) : new IndexedDbStorage(dbName));

  // 1) Vilken lokal profil (gäst eller konto) ska visas? Migrering från äldre version är icke-destruktiv.
  const auth = await AuthStore.open(async () => {
    const legacy = await makeStorage(LEGACY_DB);
    const d = await legacy.loadDevice();
    return { account: d?.account ? { serverUrl: d.account.serverUrl, email: d.account.email } : null };
  }, storageFor);
  setAuthStore(auth);
  // Service workern visar push på enhetens språk: spegla valet till registret (nu när det finns).
  const mirrorLang = () => void writeUiPrefs({ lang: getLang().snapshot.lang }).catch(() => undefined);
  mirrorLang();
  getLang().subscribe(mirrorLang);

  // 2) Profilens EGEN databas. Andra profilers data öppnas aldrig i denna sidladdning.
  const nowParam = import.meta.env.DEV || import.meta.env.MODE === 'test' ? new URLSearchParams(location.search).get('now') : null;
  const clock = nowParam ? fixedClock(nowParam) : systemClock;
  const store = new AppStore(storageFor(auth.profile.dbName), clock, { syncEnabled: auth.profile.kind === 'account' });
  setStore(store);
  await store.init();
  auth.attachStore(store);
  auth.configureApi();

  const notif = new NotificationService(store, auth);
  setNotificationService(notif);
  const sync = new SyncClient(store, auth);
  setSyncClient(sync);
  await installNativeBackNavigation();

  // 3) Kontobyte i en annan flik: ladda om så att denna flik aldrig visar eller synkar fel profil.
  onProfileChange((active) => {
    if (active !== auth.profile.id || auth.snapshot.state.status === 'guest') location.reload();
  });
  // Sida återställd från bakåt-/framåtcache: kontrollera att profilen fortfarande är aktiv.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    void readRegistry().then((r) => {
      if (r && r.activeProfileId !== auth.profile.id) location.reload();
    });
  });

  if (!isNative() && 'serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    navigator.serviceWorker.addEventListener('message', (e) => {
      const d = e.data as { type?: string; ref?: string };
      if (d?.type === 'open' && d.ref) location.hash = `#/post/${encodeURIComponent(d.ref)}`;
    });
  }

  // 4) Tillbaka till samma vy och datum efter inloggning/utloggning (endast interna vägar).
  const ret = resetting ? null : consumeReturnTo();
  if (ret) history.replaceState(null, '', ret.hash);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App initialFocusDate={ret?.focusDate ?? null} initialCalendarView={ret?.calendarView ?? null} entryIntent={entryIntent} />
    </StrictMode>,
  );
  void auth.start();
  void notif.start();
  void sync.start();
}

void boot();
